type AppUpdateOptions = {
  buildId: string
  versionUrl?: string
  intervalMs?: number
  staleAfterMs?: number
}

const RELOAD_KEY = 'lunary.reload-at'
const RELOAD_GUARD_MS = 60_000

let currentBuild = ''
let versionUrl = '/version.json'
let outdated = false
let hiddenAt: number | null = null
let started = false

export function isAppOutdated(): boolean {
  return outdated
}

function reloadGuardPassed(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY))
    if (Number.isFinite(last) && Date.now() - last < RELOAD_GUARD_MS) return false
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    return true
  }
  return true
}

export function reloadOnce(): void {
  if (reloadGuardPassed()) window.location.reload()
}

export function navigateIfOutdated(href: string): void {
  if (outdated && reloadGuardPassed()) window.location.assign(href)
}

async function checkVersion(): Promise<boolean> {
  if (outdated) return true
  try {
    const response = await fetch(`${versionUrl}?t=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return false
    const data: unknown = await response.json()
    if (typeof data !== 'object' || data === null || !('build' in data)) return false
    if (typeof data.build !== 'string' || data.build === '') return false
    outdated = data.build !== currentBuild
  } catch {
    return false
  }
  return outdated
}

export function initAppUpdate(options: AppUpdateOptions): void {
  if (started || typeof window === 'undefined') return
  started = true
  currentBuild = options.buildId
  versionUrl = options.versionUrl ?? '/version.json'
  const intervalMs = options.intervalMs ?? 10 * 60_000
  const staleAfterMs = options.staleAfterMs ?? 30 * 60_000

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    reloadOnce()
  })

  const onVisible = async () => {
    const awayMs = hiddenAt === null ? 0 : Date.now() - hiddenAt
    hiddenAt = null
    const isOutdated = await checkVersion()
    if (isOutdated && awayMs >= staleAfterMs) reloadOnce()
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') hiddenAt = Date.now()
    else void onVisible()
  })
  window.addEventListener('online', () => void checkVersion())
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void checkVersion()
  }, intervalMs)
}
